import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { generatePath, useLocation, useParams } from 'react-router-dom';
import BaseModal from '@components/common/form/BaseModal';
import ListPage from '@components/common/layout/ListPage';
import PageWrapper from '@components/common/layout/PageWrapper';
import { DEFAULT_TABLE_ITEM_SIZE } from '@constants';
import apiConfig from '@constants/apiConfig';
import useListModal from '@hooks/useListModal';
import useTranslate from '@hooks/useTranslate';
import { BaseTable } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import OrderDisplayMappingForm from '@modules/listOrderDevice/OrderDisplayMappingForm';
import routes from '@routes';
import { orderNumber } from '@utils';

const message = defineMessages({
    objectName: 'ReadyBoard Mapping',
});

const PickupDisplayMappingListPage = () => {
    const translate = useTranslate();
    const params = useParams();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const tab = queryParams.get('tab');
    const orderDisplayId = queryParams.get('orderDisplayId');
    const orderDisplayName = queryParams.get('orderDisplayName');
    const restaurantId = queryParams.get('restaurantId');
    const restaurantName = queryParams.get('restaurantName');
    const collapsePickUpId = queryParams.get('collapsePickUpId');

    const {
        data,
        mixinFuncs,
        loading,
        pagination,
        detail,
        loadingSave,
        form,
        openModal,
        onCancelModal,
        setIsChangedFormValues,
        onSave,
        isEditing,
    } = useListModal({
        apiConfig: {
            getList: apiConfig.orderDisplayMapping.getList,
            getById: apiConfig.orderDisplayMapping.getById,
            create: apiConfig.orderDisplayMapping.create,
            update: apiConfig.orderDisplayMapping.update,
            delete: apiConfig.orderDisplayMapping.delete,
        },
        options: {
            pageSize: DEFAULT_TABLE_ITEM_SIZE,
            objectName: translate.formatMessage(message.objectName),
        },
        override: (funcs) => {
            funcs.mappingData = (response) => {
                if (response.result === true) {
                    return {
                        data: response.data.content,
                        total: response.data.totalElements,
                    };
                }
            };
            funcs.prepareGetListParams = (filter) => {
                return {
                    ...filter,
                    page: filter?.page > 0 ? filter.page - 1 : 0,
                    size: DEFAULT_TABLE_ITEM_SIZE,
                    orderDisplayId,
                };
            };
        },
    });

    const handleSubmitForm = (values) => {
        const payload = { ...values };
        if (!detail?.id) {
            payload.orderDisplayId = orderDisplayId;
        }
        onSave(payload);
    };

    const columns = [
        {
            title: '#',
            dataIndex: 'index',
            key: 'id',
            width: 50,
            render: (text, record, index) => orderNumber(pagination, index),
        },
        {
            title: <FormattedMessage defaultMessage="ReadyBoard mapping number" />,
            dataIndex: 'orderDisplayNumber',
            render: (data) => {
                return (
                    <>
                        {translate.formatMessage(commonMessage.readyBoardMappingNumber)} {data}
                    </>
                );
            },
        },

        mixinFuncs.renderActionColumn(
            {
                edit: true,
                delete: true,
            },
            { width: '120px' },
        ),
    ].filter(Boolean);

    return (
        <PageWrapper
            routes={[
                {
                    breadcrumbName: translate.formatMessage(commonMessage.digibesShop),
                    path: routes.shopAccountListPage.path,
                },
                {
                    breadcrumbName: translate.formatMessage(commonMessage.restaurant),
                    path: generatePath(routes.RestaurantByCustomerListPage.path,{ customerId: params.customerId }),
                },
                {
                    breadcrumbName: restaurantName || translate.formatMessage(commonMessage.devices),
                    path: `${generatePath(routes.deviceTabsRestaurantListPage.path, {
                        customerId: params.customerId,
                    })}?tab=${tab}&restaurantId=${restaurantId}&restaurantName=${restaurantName}&collapsePickUpId=${collapsePickUpId}`,
                },
                { breadcrumbName: orderDisplayName || translate.formatMessage(message.objectName) },
            ]}
        >
            <ListPage
                actionBar={mixinFuncs.renderActionBar()}
                baseTable={
                    <BaseTable
                        onChange={mixinFuncs.changePagination}
                        columns={columns}
                        dataSource={data}
                        pagination={pagination}
                        loading={loading}
                        rowKey="id"
                    />
                }
            />
            {openModal && (
                <BaseModal
                    title={
                        detail
                            ? translate.formatMessage(commonMessage.update) +
                              ' ' +
                              translate.formatMessage(message.objectName)
                            : translate.formatMessage(commonMessage.create) +
                              ' ' +
                              translate.formatMessage(message.objectName)
                    }
                    width={400}
                    isModalVisible={openModal}
                    handleCancel={onCancelModal}
                    isEditing={!!detail}
                    form={form}
                    loading={loadingSave}
                >
                    <OrderDisplayMappingForm
                        detail={detail}
                        form={form}
                        isEditing={isEditing}
                        onSubmit={handleSubmitForm}
                        actions={mixinFuncs.renderActions()}
                        setIsChangedFormValues={setIsChangedFormValues}
                    />
                </BaseModal>
            )}
        </PageWrapper>
    );
};

export default PickupDisplayMappingListPage;
